// packages/console/src/components/panels/CostUsagePanel.tsx
//
// Cost & usage. Replaces the former "transcoder farm" hardware panel in the
// left column with the workspace's real billing-relevant meters. It binds to
// the one honest source that exists: the FT-1 usage rollup (GET /v1/usage,
// surfaced as session.usage), reporting the current UTC month against the
// plan tier's limits. R10 holds: when a figure is not yet loaded the readout
// is the em dash and the meter shows an empty idle track, never a fabricated
// zero. A real rollup value of 0 (e.g. no jobs failed this month) is a true
// zero and is shown as such.
//
// Visual language is the console's own Parts primitives (Panel / Read / Meter)
// and the colors_and_type tokens, so it sits flush with the rest of the kit.
// The exact Claude Design "Cost_and_usage" mock is reconciled against this
// once the design MCP is reachable; the data wiring below does not change.

import { Panel, Read, Meter } from "../Parts";
import { EM_DASH, fmtGB, fmtInt, fmtNum } from "../../lib/format";
import type { UsageResponse } from "../../api/tenancy";

// meterPct returns a clamped used/limit percentage, or null when the limit is
// unknown (<= 0) so the caller can render an empty track instead of a fill.
function meterPct(used: number, limit: number): number | null {
  if (!(limit > 0)) return null;
  return Math.min(100, Math.max(0, (used / limit) * 100));
}

// A label + right-aligned mono value line, with a meter beneath. usedText is
// the em dash when the underlying figure is absent.
function MeterRow({
  label,
  usedText,
  pct,
  color,
}: {
  label: string;
  usedText: string;
  pct: number | null;
  color: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span
          style={{
            font: "500 10px var(--font-sans)",
            letterSpacing: ".1em",
            textTransform: "uppercase",
            color: "var(--fg3)",
          }}
        >
          {label}
        </span>
        <span
          style={{
            font: "400 11px var(--font-mono)",
            color: "var(--fg2)",
            marginLeft: "auto",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {usedText}
        </span>
      </div>
      {pct === null ? (
        // Unknown limit: empty idle track, no fill (a 0% fill would read as
        // "0% used", a fabricated zero R10(b) forbids).
        <div style={{ height: 3, background: "var(--bg-input)", borderRadius: 1 }} />
      ) : (
        <Meter pct={pct} color={color} />
      )}
    </div>
  );
}

export function CostUsagePanel({ usage }: { usage: UsageResponse | null }) {
  const has = usage !== null;

  const hoursUsed = usage ? usage.encodeHoursUsed : null;
  const hoursLimit = usage && usage.encodeHoursLimit > 0 ? usage.encodeHoursLimit : null;
  const hoursText =
    hoursUsed === null
      ? EM_DASH
      : `${fmtNum(hoursUsed, 1)} / ${hoursLimit === null ? EM_DASH : fmtInt(hoursLimit)} h`;
  const hoursPct = usage ? meterPct(usage.encodeHoursUsed, usage.encodeHoursLimit) : null;

  const storageLimit = usage && usage.storageBytesLimit > 0 ? usage.storageBytesLimit : null;
  const storageText = !has
    ? EM_DASH
    : `${fmtGB(usage!.storageBytesUsed, 1)} / ${storageLimit === null ? EM_DASH : fmtGB(storageLimit, 0)}`;
  const storagePct = usage ? meterPct(usage.storageBytesUsed, usage.storageBytesLimit) : null;

  const planTier = usage?.planTier;

  return (
    <Panel
      title="cost & usage"
      style={{ flex: "none" }}
      right={
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            font: "400 10px var(--font-mono)",
            color: "var(--fg3)",
            whiteSpace: "nowrap",
          }}
        >
          {usage?.month ?? EM_DASH}
          <span
            style={{
              font: "600 9px var(--font-sans)",
              letterSpacing: ".1em",
              textTransform: "uppercase",
              color: has ? "var(--blue-300)" : "var(--fg4)",
              background: has ? "var(--blue-tint)" : "transparent",
              border: `1px solid ${has ? "var(--blue-line)" : "var(--line-strong)"}`,
              borderRadius: "var(--r-pill)",
              padding: "2px 8px",
            }}
          >
            {planTier || EM_DASH}
          </span>
        </span>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <MeterRow label="Encode hours" usedText={hoursText} pct={hoursPct} color="var(--viz-1)" />
        <MeterRow label="Storage" usedText={storageText} pct={storagePct} color="var(--viz-2)" />
        <div style={{ display: "flex", gap: 18 }}>
          <Read value={fmtInt(usage ? usage.jobsCompleted : null)} label="Completed" size={19} />
          <Read value={fmtInt(usage ? usage.jobsQueued : null)} label="Queued" size={19} />
          <Read
            value={fmtInt(usage ? usage.jobsFailed : null)}
            label="Failed"
            size={19}
            tone={usage && usage.jobsFailed > 0 ? "var(--err)" : undefined}
          />
        </div>
        <div style={{ display: "flex", gap: 18 }}>
          <Read value={fmtInt(usage ? usage.uploadsCompleted : null)} label="Uploads" size={17} />
          <Read value={fmtInt(usage ? usage.jobsStarted : null)} label="Started" size={17} />
        </div>
      </div>
    </Panel>
  );
}
